import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as alertThresholdsApi from '../api/alertThresholds';

export function AdminAlertThresholds() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    programId: '',
    siteId: '',
    thresholdType: '',
    warningValue: '',
    criticalValue: '',
    unit: ''
  });

  const loadThresholds = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await alertThresholdsApi.getAlertThresholds(selectedOrg.id);
      setThresholds(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets]);

  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]);

  const handleOpenCreate = () => {
    setEditingThreshold(null);
    setFormData({
      programId: 'program-nevi',
      siteId: '',
      thresholdType: 'temperature',
      warningValue: '',
      criticalValue: '',
      unit: 'celsius'
    });
    setShowModal(true);
  };

  const handleOpenEdit = (threshold) => {
    setEditingThreshold(threshold);
    setFormData({
      programId: threshold.programId,
      siteId: threshold.siteId || '',
      thresholdType: threshold.thresholdType,
      warningValue: threshold.warningValue,
      criticalValue: threshold.criticalValue,
      unit: threshold.unit || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    try {
      if (editingThreshold) {
        await alertThresholdsApi.updateAlertThreshold(selectedOrg.id, editingThreshold.id, {
          warningValue: parseFloat(formData.warningValue),
          criticalValue: parseFloat(formData.criticalValue),
          unit: formData.unit
        });
      } else {
        await alertThresholdsApi.createAlertThreshold(selectedOrg.id, {
          programId: formData.programId,
          siteId: formData.siteId || null,
          thresholdType: formData.thresholdType,
          warningValue: parseFloat(formData.warningValue),
          criticalValue: parseFloat(formData.criticalValue),
          unit: formData.unit
        });
      }
      setShowModal(false);
      loadThresholds();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (threshold) => {
    if (!selectedOrg || !isAdmin) return;
    try {
      await alertThresholdsApi.updateAlertThreshold(selectedOrg.id, threshold.id, {
        isActive: !threshold.isActive
      });
      loadThresholds();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view alert thresholds.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Alert Thresholds"
        subtitle="Configure alert thresholds per program and site"
      />

      <div style={styles.toolbar}>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            style={styles.createButton}
            data-testid="create-threshold-button"
          >
            + New Threshold
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading thresholds...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="thresholds-table">
            <thead>
              <tr>
                <th style={styles.th}>Program</th>
                <th style={styles.th}>Site Override</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Warning</th>
                <th style={styles.th}>Critical</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {thresholds.map(threshold => (
                <tr key={threshold.id} style={styles.tr} data-testid={`threshold-${threshold.id}`}>
                  <td style={styles.td}>{threshold.programId}</td>
                  <td style={styles.td}>
                    {threshold.siteId ? (
                      <span style={styles.siteOverride}>{threshold.siteId}</span>
                    ) : (
                      <span style={styles.programDefault}>Program Default</span>
                    )}
                  </td>
                  <td style={styles.td}>{threshold.thresholdType}</td>
                  <td style={styles.td}>{threshold.warningValue}</td>
                  <td style={styles.td}>{threshold.criticalValue}</td>
                  <td style={styles.td}>{threshold.unit}</td>
                  <td style={styles.td}>v{threshold.version}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: threshold.isActive ? '#c6f6d5' : '#fed7d7',
                      color: threshold.isActive ? '#276749' : '#c53030'
                    }}>
                      {threshold.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleOpenEdit(threshold)}
                          style={styles.editButton}
                          data-testid={`edit-${threshold.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(threshold)}
                          style={styles.toggleButton}
                          data-testid={`toggle-${threshold.id}`}
                        >
                          {threshold.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {thresholds.length === 0 && (
                <tr>
                  <td colSpan="9" style={styles.emptyState}>
                    No thresholds configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="threshold-modal">
            <h3 style={styles.modalTitle}>
              {editingThreshold ? 'Edit Threshold' : 'Create Threshold'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Program</label>
              <select
                value={formData.programId}
                onChange={(e) => setFormData(prev => ({ ...prev, programId: e.target.value }))}
                style={styles.select}
                disabled={!!editingThreshold}
                data-testid="threshold-program-input"
              >
                <option value="program-nevi">NEVI</option>
                <option value="program-fleet">Fleet</option>
              </select>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Site Override (optional)</label>
              <input
                type="text"
                value={formData.siteId}
                onChange={(e) => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
                style={styles.input}
                placeholder="Leave empty for program default"
                disabled={!!editingThreshold}
                data-testid="threshold-site-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Threshold Type</label>
              <select
                value={formData.thresholdType}
                onChange={(e) => setFormData(prev => ({ ...prev, thresholdType: e.target.value }))}
                style={styles.select}
                disabled={!!editingThreshold}
                data-testid="threshold-type-input"
              >
                <option value="temperature">Temperature</option>
                <option value="offline_duration">Offline Duration</option>
                <option value="power_deviation">Power Deviation</option>
                <option value="voltage">Voltage</option>
              </select>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Warning Value</label>
                <input
                  type="number"
                  value={formData.warningValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, warningValue: e.target.value }))}
                  style={styles.input}
                  data-testid="threshold-warning-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Critical Value</label>
                <input
                  type="number"
                  value={formData.criticalValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, criticalValue: e.target.value }))}
                  style={styles.input}
                  data-testid="threshold-critical-input"
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                style={styles.input}
                placeholder="e.g., celsius, minutes, percent"
                data-testid="threshold-unit-input"
              />
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowModal(false)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={styles.submitButton}
                data-testid="save-threshold-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '1rem'
  },
  createButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tableContainer: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#4a5568',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem'
  },
  siteOverride: {
    padding: '0.25rem 0.5rem',
    background: '#bee3f8',
    borderRadius: '4px',
    fontSize: '0.75rem'
  },
  programDefault: {
    color: '#718096',
    fontSize: '0.75rem',
    fontStyle: 'italic'
  },
  statusBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  editButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #3182ce',
    background: 'white',
    color: '#3182ce',
    fontSize: '0.75rem',
    cursor: 'pointer',
    marginRight: '0.5rem'
  },
  toggleButton: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #718096',
    background: 'white',
    color: '#718096',
    fontSize: '0.75rem',
    cursor: 'pointer'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    minWidth: '450px',
    maxWidth: '550px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  modalTitle: {
    margin: '0 0 1.5rem 0',
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  formGroup: {
    marginBottom: '1rem',
    flex: 1
  },
  formRow: {
    display: 'flex',
    gap: '1rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#4a5568'
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem'
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '1.5rem'
  },
  cancelButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #3182ce',
    background: '#3182ce',
    color: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer'
  }
};
